<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  import BankSelectorModal from '../components/BankSelectorModal.svelte';
  import CreditCardSelectorModal from '../components/AdvancedCreditCardSelectorModal.svelte';
  import PaymentMethodSelectorModal from '../components/PaymentMethodSelectorModal.svelte';
  import CategorySelectorModal from '../components/CategorySelectorModal.svelte';
  import StoreSelectorModal from '../components/StoreSelectorModal.svelte';
  import PlaceSelectorModal from '../components/PlaceSelectorModal.svelte';
  import PromotionFormulaModal from '../components/PromotionFormulaModal.svelte';
  import { cloneStore } from '../cloneStore.js';
  import { getCurrencySymbol } from '../utils';
  import { authFetch } from '../authService'; // <-- IMPORT THIS

  export let id; // From the router path /edit/:id

  // --- Form State Variables ---
  let name = '';
  let description = '';
  let brand = '';
  let promoCode = '';
  let startDate = '';
  let endDate = '';
  let categories = [];
  let hyperlink = '';
  let images = []; 
  let banks = [];
  let creditCards = [];
  let methodsOfSpending = [];

  let isOnline = false;
  let onlineShops = '';
  let isInStore = false;
  let physicalStores = []; // Changed from string to array
  let places = [];
  
  // Spending & Achievement Vars
  let minimumSpending = '';
  let targetNumTransactions = '';
  let minimumTargetAccumulatedSpending = '';
  let maximumTargetNumTransactions = '';
  let maximumTargetAccumulatedSpending = '';

  // Reward Fields
  let originalPrice = '';
  let discountPrice = '';
  let maximumReward = '';
  let maximumRewardPercentage = '';

  // --- NEW STATE FOR SCHEDULING ---
    let eligibleDaysOfWeek = []; 
    let eligibleDates = [];
    let tempDate = ''; // Temporary holder for the date picker
    let ineligibleDates = [];
    let tempIneligibleDate = '';
  
  // Rules & Tiers
  let rewardScheme = []; // Frequency based
  let rewardTiers = [];  // Condition based

  let requireRegistration = false;
  let registrationDetail = '';
  let includeSelectedMember = false;
  let selectedMember = false;

  // Recurrence Rule State
  let recurrenceEnabled = false;
  let recurrenceFrequency = 'weekly';
  let recurrenceEndDate = '';
  let recurrenceLeadDays = 2;
  let recurrenceAutoPublish = true;
  let recurrenceNamingPattern = '';
  
  // Modal State
  let showBankModal = false;
  let showCreditCardModal = false;
  let showPaymentMethodModal = false;
  let showCategoryModal = false;
  let showStoreModal = false;
  let showPlaceModal = false;
  let showFormulaModal = false;

  let currentUser = null;
  let isAdmin = false;
  let isSuperAdmin = false;

  // Constant for rendering days
    const daysOfWeekList = [
        { id: 0, label: 'Sunday' },
        { id: 1, label: 'Monday' },
        { id: 2, label: 'Tuesday' },
        { id: 3, label: 'Wednesday' },
        { id: 4, label: 'Thursday' },
        { id: 5, label: 'Friday' },
        { id: 6, label: 'Saturday' }
    ];

    // Subscribe to get role
  authStore.subscribe(val => {
      currentUser = val.user;
      isAdmin = val.isAdmin;
      isSuperAdmin = val.isSuperAdmin;
  });

  // Access Control for Formulas
  $: canManageFormulas = isAdmin || isSuperAdmin;

  // Submission State
  let error = '';
  let isSubmitting = false;
  let isLoadingData = true;
  let isUploadingImage = false;
  
  // Currency Display
  let currencySymbol = '$';

  // Helper to format date from ISO string to YYYY-MM-DD for date input
  function formatDateForInput(isoString) {
    if (!isoString) return '';
    return isoString.split('T')[0];
  }

  onMount(async () => {
    try {
      const response = await fetch(getApiUrl(`/promotions/${id}`));
      if (!response.ok) throw new Error('Promotion not found.');
      const promo = await response.json();

      // Set currency based on the record
      currencySymbol = getCurrencySymbol(promo.countryCode);

      // Populate form state with fetched data
      name = promo.name || '';
      description = promo.description || '';
      brand = promo.brand || '';
      promoCode = promo.promoCode || '';
      startDate = formatDateForInput(promo.startDate);
      endDate = formatDateForInput(promo.endDate);
      // Safety check: ensure images is an array to prevent .length crashes
      images = Array.isArray(promo.images) ? promo.images : [];

      categories = promo.categories || [];
      hyperlink = promo.hyperlink || '';
      banks = promo.banks || [];
      creditCards = promo.creditCards || [];
      methodsOfSpending = promo.methodsOfSpending || [];
      
      isOnline = promo.isOnline || false;
      onlineShops = (promo.onlineShops || []).join(', ');
      isInStore = promo.isInStore || false;
      physicalStores = promo.physicalStores || [];
      places = promo.places || [];
      
      minimumSpending = promo.minimumSpending != null ? promo.minimumSpending : '';
      targetNumTransactions = promo.targetNumTransactions != null ? promo.targetNumTransactions : '';
      minimumTargetAccumulatedSpending = promo.minimumTargetAccumulatedSpending != null ? promo.minimumTargetAccumulatedSpending : '';
      maximumTargetNumTransactions = promo.maximumTargetNumTransactions != null ? promo.maximumTargetNumTransactions : '';
      maximumTargetAccumulatedSpending = promo.maximumTargetAccumulatedSpending != null ? promo.maximumTargetAccumulatedSpending : '';
      
      requireRegistration = promo.requireRegistration || false;
      registrationDetail = promo.registrationDetail || '';
      if (promo.selectedMember != null) {
        includeSelectedMember = true;
        selectedMember = promo.selectedMember;
      }
      
      rewardScheme = promo.rewardScheme || [];
      rewardTiers = promo.rewardTiers || [];
      
      originalPrice = promo.originalPrice != null ? promo.originalPrice : '';
      discountPrice = promo.discountPrice != null ? promo.discountPrice : '';
      maximumReward = promo.maximumReward != null ? promo.maximumReward : '';
      maximumRewardPercentage = promo.maximumRewardPercentage != null ? promo.maximumRewardPercentage : '';

      eligibleDaysOfWeek = promo.eligibleDaysOfWeek || [];
      eligibleDates = promo.eligibleDates || [];
      ineligibleDates = promo.ineligibleDates || [];

      if (promo.recurrenceRule) {
        recurrenceEnabled = promo.recurrenceRule.enabled !== undefined ? promo.recurrenceRule.enabled : false;
        recurrenceFrequency = promo.recurrenceRule.frequency || 'weekly';
        recurrenceEndDate = formatDateForInput(promo.recurrenceRule.endDate);
        recurrenceLeadDays = promo.recurrenceRule.leadDays || 2;
        recurrenceAutoPublish = promo.recurrenceRule.autoPublish !== undefined ? promo.recurrenceRule.autoPublish : true;
        recurrenceNamingPattern = promo.recurrenceRule.namingPattern || '';
      }

    } catch (e) {
      error = e.message;
    } finally {
      isLoadingData = false;
    }
  });

  // --- Reactive Calculations ---
  $: {
    const orig = parseFloat(originalPrice);
    const disc = parseFloat(discountPrice);
    if (orig > 0 && disc >= 0 && orig > disc) {
      const reward = orig - disc;
      const percent = (reward / orig) * 100;
      maximumReward = reward.toFixed(2);
      maximumRewardPercentage = percent.toFixed(2);
    }
  }

  function toggleDay(dayId) {
        if (eligibleDaysOfWeek.includes(dayId)) {
            eligibleDaysOfWeek = eligibleDaysOfWeek.filter(d => d !== dayId);
        } else {
            eligibleDaysOfWeek = [...eligibleDaysOfWeek, dayId];
        }
    }

    function addEligibleDate() {
        if (!tempDate) return;
        
        if (eligibleDates.includes(tempDate)) {
            alert("This date is already added.");
            return;
        }

        // VALIDATION: Ensure specific date is within the promotion period
        if (startDate && endDate) {
            const checkDate = new Date(tempDate);
            // Zero out time components for fair comparison
            checkDate.setHours(0,0,0,0);
            
            const start = new Date(startDate);
            start.setHours(0,0,0,0);
            
            const end = new Date(endDate);
            end.setHours(23,59,59,999);

            if (checkDate < start || checkDate > end) {
                alert("Specific date must fall within the Start Date and End Date of the promotion.");
                return;
            }
        } else {
            alert("Please set a Start Date and End Date first.");
            return;
        }

        eligibleDates = [...eligibleDates, tempDate];
        tempDate = ''; // Reset input
    }

    function removeEligibleDate(dateToRemove) {
        eligibleDates = eligibleDates.filter(date => date !== dateToRemove);
    }

    function addIneligibleDate() {
        if (!tempIneligibleDate) return;
        
        if (ineligibleDates.includes(tempIneligibleDate)) {
            alert("This date is already added.");
            return;
        }

        // VALIDATION: Ensure specific date is within the promotion period
        if (startDate && endDate) {
            const checkDate = new Date(tempIneligibleDate);
            checkDate.setHours(0,0,0,0);
            
            const start = new Date(startDate);
            start.setHours(0,0,0,0);
            
            const end = new Date(endDate);
            end.setHours(23,59,59,999);

            if (checkDate < start || checkDate > end) {
                alert("Ineligible date must fall within the Start Date and End Date of the promotion.");
                return;
            }
        } else {
            alert("Please set a Start Date and End Date first.");
            return;
        }

        ineligibleDates = [...ineligibleDates, tempIneligibleDate];
        tempIneligibleDate = ''; // Reset input
    }

    function removeIneligibleDate(dateToRemove) {
        ineligibleDates = ineligibleDates.filter(date => date !== dateToRemove);
    }

  function handleClone() {
    const promotionToClone = {
      id,
      name: `${name} (Copy)`,
      description,
      brand,
      promoCode,
      startDate,
      endDate,
      images,
      categories,
      hyperlink,
      banks,
      creditCards,
      methodsOfSpending,
      isOnline,
      onlineShops,
      isInStore,
      physicalStores,
      places,
      minimumSpending,
      targetNumTransactions,
      minimumTargetAccumulatedSpending,
      maximumTargetNumTransactions,
      maximumTargetAccumulatedSpending,
      requireRegistration,
      registrationDetail,
      includeSelectedMember,
      selectedMember,
      rewardScheme,
      rewardTiers,
      originalPrice,
      discountPrice,
      maximumReward,
      maximumRewardPercentage,
      eligibleDaysOfWeek, // NEW
      eligibleDates,       // NEW
      ineligibleDates     // NEW
    };

    cloneStore.set(promotionToClone);
    navigate('/add');
  }

  async function handleSubmit() {
    isSubmitting = true;
    error = '';
    
    if (!name || !startDate || !endDate || !description) {
      error = 'Name, Start Date, End Date, and Description are required.';
      isSubmitting = false; return;
    }

    // --- FIX: Force UTC Date Construction ---
    const startDateUTC = `${startDate}T00:00:00Z`;
    const endDateUTC = `${endDate}T23:59:59Z`;

    const payload = {
      name,
      description,
      brand,
      promoCode,
      startDate: startDateUTC,
      endDate: endDateUTC,
      images,
      categories,
      banks,
      creditCards,
      methodsOfSpending,
      requireRegistration,
      isOnline,
      isInStore,
      eligibleDaysOfWeek, // NEW
      eligibleDates,       // NEW
      ineligibleDates,     // NEW
      
      // Map Schemes
      rewardScheme: rewardScheme.map(rule => ({
        frequency: parseInt(rule.frequency) || 1,
        rewardType: rule.rewardType,
        fixedAmount: rule.rewardType === 'Fixed' ? parseFloat(rule.fixedAmount) || 0 : null,
        percentage: rule.rewardType === 'Percentage' ? parseFloat(rule.percentage) || 0 : null,
        maxRewardAmount: rule.rewardType === 'Percentage' ? parseFloat(rule.maxRewardAmount) || 0 : null,
      })),

      // Map Tiers
      rewardTiers: rewardTiers.map(t => ({
          condition: t.condition,
          rewardType: t.rewardType,
          fixedAmount: t.rewardType === 'Fixed' ? parseFloat(t.fixedAmount) || 0 : null,
          percentage: t.rewardType === 'Percentage' ? parseFloat(t.percentage) || 0 : null,
          unitOfMeasure: t.rewardType === 'Fixed' ? t.unitOfMeasure : null,
          topupAmount: t.topupAmount ? parseFloat(t.topupAmount) || 0 : null
      })),
      
      // Price/Reward fields
      originalPrice: originalPrice ? parseFloat(originalPrice) : null,
      discountPrice: discountPrice ? parseFloat(discountPrice) : null,
      maximumReward: maximumReward ? parseFloat(maximumReward) : null,
      maximumRewardPercentage: maximumRewardPercentage ? parseFloat(maximumRewardPercentage) : null,
    };
    
    // Add optional fields...
    if (hyperlink.trim()) payload.hyperlink = hyperlink;
    if (isOnline) payload.onlineShops = onlineShops.split(',').map(s => s.trim()).filter(Boolean);
    
    //if (isInStore) payload.physicalStores = physicalStores.split(',').map(s => s.trim()).filter(Boolean);
    if (isInStore) payload.physicalStores = physicalStores;
    if (isInStore) payload.places = places;

    if (requireRegistration) payload.registrationDetail = registrationDetail;
    if (includeSelectedMember) payload.selectedMember = selectedMember; else payload.selectedMember = null;
    if (minimumSpending) payload.minimumSpending = parseFloat(minimumSpending); else payload.minimumSpending = null;
    if (targetNumTransactions) payload.targetNumTransactions = parseInt(targetNumTransactions, 10); else payload.targetNumTransactions = null;
    if (minimumTargetAccumulatedSpending) payload.minimumTargetAccumulatedSpending = parseFloat(minimumTargetAccumulatedSpending); else payload.minimumTargetAccumulatedSpending = null;
    if (maximumTargetNumTransactions) payload.maximumTargetNumTransactions = parseInt(maximumTargetNumTransactions, 10); else payload.maximumTargetNumTransactions = null;
    if (maximumTargetAccumulatedSpending) payload.maximumTargetAccumulatedSpending = parseFloat(maximumTargetAccumulatedSpending); else payload.maximumTargetAccumulatedSpending = null;

    if (recurrenceEnabled) {
      payload.recurrenceRule = {
        enabled: true,
        frequency: recurrenceFrequency,
        leadDays: parseInt(recurrenceLeadDays, 10) || 2,
        autoPublish: recurrenceAutoPublish,
        namingPattern: recurrenceNamingPattern.trim()
      };
      if (recurrenceEndDate) {
        payload.recurrenceRule.endDate = `${recurrenceEndDate}T23:59:59Z`;
      }
    } else {
      payload.recurrenceRule = {
        enabled: false,
        frequency: recurrenceFrequency,
        leadDays: parseInt(recurrenceLeadDays, 10) || 2,
        autoPublish: recurrenceAutoPublish
      };
      if (recurrenceEndDate) {
        payload.recurrenceRule.endDate = `${recurrenceEndDate}T23:59:59Z`;
      }
    }

    try {
      const response = await authFetch(getApiUrl(`/promotions/${id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        // Handle Permission Error
        if (response.status === 403) {
            throw new Error("You do not have permission to edit this promotion.");
        }
        throw new Error(errData.error || "Failed to update promotion.");
      }
      
      navigate("/", { replace: true });
    } catch (e) {
      error = e.message;
    } finally {
      isSubmitting = false;
    }
  }

  // --- Modal and Rule Handlers ---
  function handleBankConfirm(event) { banks = event.detail.selected; showBankModal = false; }
  function handleCreditCardConfirm(event) { creditCards = event.detail.selected; showCreditCardModal = false; }
  function handlePaymentMethodConfirm(event) { methodsOfSpending = event.detail.selected; showPaymentMethodModal = false; }
  function handleCategoryConfirm(event) { categories = event.detail.selected; showCategoryModal = false; }
  function handleStoreConfirm(event) {
      physicalStores = event.detail.selected;
      showStoreModal = false;
  }
  function handlePlaceConfirm(event) {
      places = event.detail.selected;
      showPlaceModal = false;
  }

  // Reward Scheme
  function addRule() {
    rewardScheme = [
      ...rewardScheme,
      { frequency: 1, rewardType: 'Percentage', percentage: 0, maxRewardAmount: 9999 }
    ];
  }
  function removeRule(index) {
    rewardScheme = rewardScheme.filter((_, i) => i !== index);
  }

  // Reward Tiers
  function addTier() {
    rewardTiers = [
      ...rewardTiers,
      { condition: '', rewardType: 'Percentage', percentage: 0, fixedAmount: 0, unitOfMeasure: 'Points', topupAmount: 0 }
    ];
  }
  function removeTier(index) {
    rewardTiers = rewardTiers.filter((_, i) => i !== index);
  }

  // Back Function ---
  function goBack() {
      window.history.back();
  }

  async function handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('image', file);

      isUploadingImage = true;
      error = '';

      try {
          const res = await authFetch(getApiUrl('/promotions/upload-image'), {
              method: 'POST',
              body: formData
          });
          const data = await res.json();
          
          if (!res.ok) throw new Error(data.error || "Failed to upload image");

          // Push new image URL to array
          images = [...images, data.imageUrl];
          
      } catch (e) {
          error = e.message;
      } finally {
          isUploadingImage = false;
          event.target.value = ''; // Reset input
      }
  }

  function removeImage(index) {
      images = images.filter((_, i) => i !== index);
  }
</script>

<BankSelectorModal bind:showModal={showBankModal} preSelectedBanks={banks} on:confirm={handleBankConfirm} on:close={() => showBankModal = false} />
<CreditCardSelectorModal bind:showModal={showCreditCardModal} preSelectedCards={creditCards} on:confirm={handleCreditCardConfirm} on:close={() => showCreditCardModal = false} />
<PaymentMethodSelectorModal bind:showModal={showPaymentMethodModal} preSelectedMethods={methodsOfSpending} on:confirm={handlePaymentMethodConfirm} on:close={() => showPaymentMethodModal = false} />
<CategorySelectorModal bind:showModal={showCategoryModal} preSelectedCategories={categories} on:confirm={handleCategoryConfirm} on:close={() => showCategoryModal = false} />
<StoreSelectorModal 
    bind:showModal={showStoreModal} 
    preSelectedStores={physicalStores} 
    on:confirm={handleStoreConfirm} 
    on:close={() => showStoreModal = false} 
/>
<PlaceSelectorModal 
    bind:showModal={showPlaceModal} 
    preSelectedPlaces={places} 
    on:confirm={handlePlaceConfirm} 
    on:close={() => showPlaceModal = false} />

<PromotionFormulaModal 
    bind:showModal={showFormulaModal} 
    promotionId={id} 
    on:close={() => showFormulaModal = false} 
/>


<div class="form-container">

  <!-- NEW: Back Button -->
  <button type="button" class="back-btn" on:click={goBack}>← Back</button>

  <div class="header-actions">
      <h2>Edit Promotion</h2>
      {#if canManageFormulas}
        <button type="button" class="formula-btn" on:click={() => showFormulaModal = true}>⚙️ Manage Reward Formulas</button>
      {/if}
  </div>
  
  {#if isLoadingData}
    <p>Loading promotion details...</p>
  {:else if error && !name}
    <p class="error">{error}</p>
  {:else}
    <form on:submit|preventDefault={handleSubmit}>
      <fieldset>
        <legend>Basic Information (Required)</legend>
        <div class="form-group"><label for="name">Promotion Name</label><input type="text" id="name" bind:value={name} /></div>
        <div class="form-group"><label for="description">Description (max 1000 chars)</label><textarea id="description" rows="4" bind:value={description}></textarea></div>
        <div class="form-group"><label for="name">Brand</label><input type="text" id="brand" bind:value={brand} /></div>
        <div class="form-group"><label for="start-date">Start Date</label><input type="date" id="start-date" bind:value={startDate} /></div>
        <div class="form-group"><label for="end-date">End Date</label><input type="date" id="end-date" bind:value={endDate} /></div>
        <div class="form-group"><label for="promo-code">Promo Code (Optional)</label><input type="text" id="promo-code" bind:value={promoCode} placeholder="e.g. SUMMER20" /></div>        
        <div class="form-group">
          <label>Categories</label>
          <div class="selection-display">{categories.join(', ') || 'No categories selected'}</div>
          <button type="button" class="select-btn" on:click={() => showCategoryModal = true}>Select Categories</button>
        </div>
      </fieldset>
      <fieldset class="schedule-fieldset">
        <legend>Special Scheduling (Optional)</legend>
        <p class="help-text">Leave these blank if the promotion is valid <strong>every day</strong> during the promotion period.</p>

        <!-- 1. Eligible Days of Week -->
        <div class="form-group">
            <label>Eligible Days of the Week</label>
            <div class="days-grid">
                {#each daysOfWeekList as day}
                    <label class="day-checkbox">
                        <input type="checkbox" 
                               checked={eligibleDaysOfWeek.includes(day.id)}
                               on:change={() => toggleDay(day.id)} />
                        {day.label}
                    </label>
                {/each}
            </div>
        </div>

        <!-- 2. Specific Eligible Dates -->
        <div class="form-group">
            <label>Specific Eligible Dates</label>
            <div class="date-input-group">
                <input type="date" bind:value={tempDate} />
                <button type="button" class="btn-add-date" on:click={addEligibleDate}>Add Date</button>
            </div>
            
            <!-- Display Selected Dates -->
            {#if eligibleDates.length > 0}
                <div class="tags-container">
                    {#each eligibleDates as date}
                        <span class="tag">
                            📅 {date}
                            <button type="button" class="remove-tag" on:click={() => removeEligibleDate(date)} title="Remove">&times;</button>
                        </span>
                    {/each}
                </div>
            {/if}
        </div>

        <!-- 3. Specific Ineligible Dates -->
        <div class="form-group">
            <label>Specific Ineligible Dates</label>
            <div class="date-input-group">
                <input type="date" bind:value={tempIneligibleDate} />
                <button type="button" class="btn-add-date" on:click={addIneligibleDate}>Add Date</button>
            </div>
            
            <!-- Display Selected Dates -->
            {#if ineligibleDates.length > 0}
                <div class="tags-container">
                    {#each ineligibleDates as date}
                        <span class="tag tag-ineligible">
                            🚫 {date}
                            <button type="button" class="remove-tag" on:click={() => removeIneligibleDate(date)} title="Remove">&times;</button>
                        </span>
                    {/each}
                </div>
            {/if}
        </div>
    </fieldset>

      <!-- IMAGE UPLOAD SECTION -->
      <fieldset>
        <div class="form-group image-section">
            <label>Promotion Images (Azure Hosted)</label>
            
            <div class="image-grid">
                {#each images as img, i}
                    <div class="image-preview">
                        <img src={img} alt="Promo preview" />
                        <button type="button" class="remove-btn" on:click={() => removeImage(i)}>&times;</button>
                    </div>
                {/each}
                
                <div class="upload-btn-wrapper">
                    <button type="button" class="upload-btn" disabled={isUploadingImage}>
                        {isUploadingImage ? 'Uploading...' : '+ Add Image'}
                    </button>
                    <input type="file" accept="image/*" on:change={handleImageUpload} disabled={isUploadingImage} />
                </div>
            </div>
        </div>
      </fieldset>

      <fieldset>
        <legend>Availability</legend>
        <div class="checkbox-group"><input type="checkbox" id="is-online" bind:checked={isOnline} /><label for="is-online">Available Online?</label></div>
        {#if isOnline}
          <div class="form-group conditional"><label for="online-shops">Online Shops (comma-separated)</label><input type="text" id="online-shops" bind:value={onlineShops} /></div>
        {/if}
        <div class="checkbox-group"><input type="checkbox" id="is-in-store" bind:checked={isInStore} /><label for="is-in-store">Available In-Store?</label></div>

        {#if isInStore}
            <div class="form-group conditional">
                <label>Physical Stores</label>
                <div class="selection-display">{physicalStores.join(', ') || 'No stores selected'}</div>
                <button type="button" class="select-btn" on:click={() => showStoreModal = true}>Select Stores</button>
            </div>
            <div class="form-group conditional">
              <label>Places (Malls/Districts)</label>
              <div class="selection-display">
                  {places.length > 0 ? places.join(', ') : 'No places selected'}
              </div>
              <button type="button" class="select-btn" on:click={() => showPlaceModal = true}>Select Places</button>
          </div>
        {/if}
      </fieldset>
      
      <!-- Payment Options Group -->
      <fieldset>
        <legend>Payment Options</legend>
        <div class="form-group">
          <label>Participating Banks</label>
          <div class="selection-display">{banks.join(', ') || 'No banks selected'}</div>
          <button type="button" class="select-btn" on:click={() => showBankModal = true}>Select Banks</button>
        </div>
        <div class="form-group">
          <label>Eligible Credit Cards</label>
          <div class="selection-display">{creditCards.join(', ') || 'No credit cards selected'}</div>
          <button type="button" class="select-btn" on:click={() => showCreditCardModal = true}>Select Credit Cards</button>
        </div>
        <div class="form-group">
          <label>Methods of Spending</label>
          <div class="selection-display">{methodsOfSpending.join(', ') || 'No methods selected'}</div>
          <button type="button" class="select-btn" on:click={() => showPaymentMethodModal = true}>Select Methods</button>
        </div>
      </fieldset>

      <!-- Spending & Achievement Rules -->
      <fieldset>
          <legend>Spending & Achievement Rules</legend>
          <div class="form-group"><label for="minimum-spending">Minimum Spending Per Transaction ({currencySymbol})</label><input type="number" step="0.01" min="0" id="minimum-spending" bind:value={minimumSpending} /></div>
          <div class="form-group"><label for="target-num-transactions">Target Number of Transactions</label><input type="number" min="0" id="target-num-transactions" bind:value={targetNumTransactions} /></div>
          <div class="form-group"><label for="min-target-spending">Minimum Target Accumulated Spending ({currencySymbol})</label><input type="number" step="0.01" min="0" id="min-target-spending" bind:value={minimumTargetAccumulatedSpending} /></div>
          <div class="form-group"><label for="maximum-target-num-transactions">Maximum Target Number of Transactions</label><input type="number" min="0" id="maximum-target-num-transactions" bind:value={maximumTargetNumTransactions} /></div>
          <div class="form-group"><label for="maximum-target-spending">Maximum Target Accumulated Spending ({currencySymbol})</label><input type="number" step="0.01" min="0" id="maximum-target-spending" bind:value={maximumTargetAccumulatedSpending} /></div>
      </fieldset>

      <!-- Simple Price Reward -->
      <fieldset>
        <legend>Simple Price Reward (Optional)</legend>
        <p>Use this for simple "Was {currencySymbol}X, Now {currencySymbol}Y" deals.</p>
        <div class="form-group">
            <label for="original-price">Original Price ({currencySymbol})</label>
            <input type="number" step="0.01" min="0" id="original-price" bind:value={originalPrice} />
        </div>
        <div class="form-group">
            <label for="discount-price">Discount Price ({currencySymbol})</label>
            <input type="number" step="0.01" min="0" id="discount-price" bind:value={discountPrice} />
        </div>
        <div class="form-group">
            <label for="maximum-reward">Maximum Reward ({currencySymbol})</label>
            <input type="number" step="0.01" min="0" id="maximum-reward" bind:value={maximumReward} placeholder="Calculated automatically" />
        </div>
        <div class="form-group">
            <label for="maximum-reward-percentage">Maximum Reward Percentage (%)</label>
            <input type="number" step="0.01" min="0" id="maximum-reward-percentage" bind:value={maximumRewardPercentage} placeholder="Calculated automatically" />
        </div>
      </fieldset>
      
      <!-- Special Reward Tiers -->
      <fieldset>
        <legend>Special Reward Tiers (Optional)</legend>
        <p>Use this for conditional rewards (e.g. "Spend $5000 to get 500 Miles").</p>
        {#if rewardTiers.length === 0}
          <p class="no-rules-text">No reward tiers defined.</p>
        {/if}
        {#each rewardTiers as tier, index}
          <div class="rule-box tier-box">
            <div class="rule-header">
              <span>Tier {index + 1}</span>
              <button type="button" class="remove-btn" on:click={() => removeTier(index)}>Remove</button>
            </div>
            <div class="form-group">
                <label>Condition</label>
                <input type="text" bind:value={tier.condition} placeholder="e.g. Spend over $5000" />
            </div>
            <div class="form-group">
              <label>Reward Type:</label>
              <select bind:value={tier.rewardType}>
                <option value="Percentage">Percentage</option>
                <option value="Fixed">Fixed Amount</option>
              </select>
            </div>
            <div class="form-group">
              <label>Topup Amount</label>
              <input type="number" step="0.01" min="0" bind:value={tier.topupAmount} placeholder="e.g. 50.00" />
            </div>
            {#if tier.rewardType === 'Percentage'}
              <div class="form-group conditional">
                <label>Percentage (%)</label>
                <input type="number" step="0.01" min="0" bind:value={tier.percentage} />
              </div>
            {:else if tier.rewardType === 'Fixed'}
              <div class="row">
                  <div class="form-group conditional">
                      <label>Amount</label>
                      <input type="number" step="0.01" min="0" bind:value={tier.fixedAmount} />
                  </div>
                  <div class="form-group conditional">
                      <label>Unit</label>
                      <input type="text" bind:value={tier.unitOfMeasure} placeholder="Miles/Points" />
                  </div>
              </div>
            {/if}
          </div>
        {/each}
        <button type="button" class="add-rule-btn" on:click={addTier}>+ Add Reward Tier</button>
      </fieldset>

      <!-- Advanced Reward Scheme -->
      <fieldset>
        <legend>Advanced Reward Scheme (Optional)</legend>
        <p>Use this for frequency rules (e.g. "Every 4th transaction gets {currencySymbol}10 back").</p>
        {#if rewardScheme.length === 0}
          <p class="no-rules-text">No reward rules defined.</p>
        {/if}
        {#each rewardScheme as rule, index}
          <div class="rule-box">
            <div class="rule-header">
              <span>Rule {index + 1}</span>
              <button type="button" class="remove-btn" on:click={() => removeRule(index)}>Remove</button>
            </div>
            <div class="form-group">
              <label>Frequency: Every</label>
              <input type="number" min="1" bind:value={rule.frequency} />
              <span>eligible transaction</span>
            </div>
            <div class="form-group">
              <label>Reward Type:</label>
              <select bind:value={rule.rewardType}>
                <option value="Percentage">Percentage</option>
                <option value="Fixed">Fixed Amount</option>
              </select>
            </div>
            {#if rule.rewardType === 'Percentage'}
              <div class="form-group conditional">
                <label>Percentage (%)</label>
                <input type="number" step="0.01" min="0" bind:value={rule.percentage} />
              </div>
              <div class="form-group conditional">
                <label>Max Reward Amount ({currencySymbol})</label>
                <input type="number" step="0.01" min="0" bind:value={rule.maxRewardAmount} />
              </div>
            {:else if rule.rewardType === 'Fixed'}
              <div class="form-group conditional">
                <label>Fixed Amount ({currencySymbol})</label>
                <input type="number" step="0.01" min="0" bind:value={rule.fixedAmount} />
              </div>
            {/if}
          </div>
        {/each}
        <button type="button" class="add-rule-btn" on:click={addRule}>+ Add Reward Rule</button>
      </fieldset>

      <fieldset>
          <legend>Registration & Membership</legend>
          <div class="checkbox-group"><input type="checkbox" id="require-registration" bind:checked={requireRegistration} /><label for="require-registration">Requires Registration?</label></div>
          {#if requireRegistration}
              <div class="form-group conditional"><label for="registration-detail">Registration Detail (Required)</label><textarea id="registration-detail" bind:value={registrationDetail}></textarea></div>
          {/if}
          <div class="checkbox-group"><input type="checkbox" id="include-selected-member" bind:checked={includeSelectedMember} /><label for="include-selected-member">Specify Selected Member Status?</label></div>
          {#if includeSelectedMember}
              <div class="checkbox-group conditional"><input type="checkbox" id="selected-member" bind:checked={selectedMember} /><label for="selected-member">For Selected Members Only?</label></div>
          {/if}
      </fieldset>

      <fieldset>
        <legend>🔁 Recurrence Rule (循環優惠設定)</legend>
        <div class="checkbox-group">
          <input type="checkbox" id="recurrence-enabled" bind:checked={recurrenceEnabled} />
          <label for="recurrence-enabled">Enable Auto-Rollover for this Promotion</label>
        </div>

        {#if recurrenceEnabled}
          <div class="form-group conditional">
            <label for="recurrence-freq">Frequency</label>
            <select id="recurrence-freq" bind:value={recurrenceFrequency}>
              <option value="weekly">Weekly (+7 Days)</option>
              <option value="monthly">Monthly (Calendar Month)</option>
              <option value="biweekly">Bi-weekly (+14 Days)</option>
            </select>
          </div>

          <div class="form-group conditional" style="background: #fefce8; padding: 0.75rem; border-radius: 6px; border: 1px solid #fef08a;">
            <label for="recurrence-end-date"><strong>Recurrence End Date (Campaign Cutoff)</strong></label>
            <input type="date" id="recurrence-end-date" bind:value={recurrenceEndDate} />
            <small style="display:block; color:#6b7280; font-size: 0.75rem; margin-top: 4px;">
              The recurrence engine will terminate and stop creating new cycles after this date. Leave blank for continuous recurrence.
            </small>
          </div>

          <div class="form-group conditional">
            <label for="recurrence-lead-days">Lead Days (Rollover Trigger)</label>
            <input type="number" id="recurrence-lead-days" min="1" max="14" bind:value={recurrenceLeadDays} />
            <small style="display:block; color:#6b7280; font-size: 0.75rem; margin-top: 4px;">Days before current promotion ends to generate the next cycle (Default: 2).</small>
          </div>

          <div class="checkbox-group conditional">
            <input type="checkbox" id="recurrence-autopublish" bind:checked={recurrenceAutoPublish} />
            <label for="recurrence-autopublish">Auto-publish new cycles as Verified (uncheck for Pending Review)</label>
          </div>

          <div class="form-group conditional">
            <label for="recurrence-pattern">Custom Naming Pattern (Optional)</label>
            <input type="text" id="recurrence-pattern" placeholder="e.g. 大家樂 樂賞錢 - &#123;&#123;startDate:D/M/YYYY&#125;&#125;" bind:value={recurrenceNamingPattern} />
            <small style="display:block; color:#6b7280; font-size: 0.75rem; margin-top: 4px;">Placeholders: <code>{'{'}{'{'}startDate:D/M/YYYY{'}'}{'}'}</code>, <code>{'{'}{'{'}startDate:YYYY年M月{'}'}{'}'}</code>. If blank, smart regex auto-increments the existing dates.</small>
          </div>
        {/if}
      </fieldset>

      <fieldset>
        <legend>Additional Details</legend>
        <div class="form-group"><label for="hyperlink">Hyperlink</label><input type="url" id="hyperlink" bind:value={hyperlink} placeholder="https://example.com" /></div>
      </fieldset>

      <div class="form-actions">
        <button type="button" class="clone-btn" on:click={handleClone}>
          Clone to New
        </button>
        <button type="submit" disabled={isSubmitting}>
          {#if isSubmitting} Saving... {:else} Save Changes {/if}
        </button>
      </div>
      {#if error} <p class="error">{error}</p> {/if}
    </form>
  {/if}
</div>

<style>
  .back-btn {
      background: none;
      border: none;
      color: #6c757d;
      font-size: 1rem;
      cursor: pointer;
      padding: 0;
      margin-bottom: 1rem;
      font-weight: 500;
  }
  .back-btn:hover {
      color: #007bff;
      text-decoration: underline;
  }

  .form-container { max-width: 700px; margin: 2rem auto; }
  form { display: flex; flex-direction: column; gap: 1rem; }
  fieldset { border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; margin-bottom: 1rem; }
  legend { font-weight: bold; color: #0056b3; padding: 0 0.5rem; }
  .form-group { margin-bottom: 1rem; }
  label { display: block; margin-bottom: 0.5rem; font-weight: 500; }
  input, textarea, select { width: 100%; padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; font-size: 1rem; box-sizing: border-box; font-family: inherit; }
  button { padding: 0.75rem; background-color: #007bff; color: white; border: none; border-radius: 4px; font-size: 1.1rem; cursor: pointer; }
  button:disabled { background-color: #aaa; }
  .error { color: red; text-align: center; }
  .checkbox-group { display: flex; align-items: center; gap: 0.5rem; }
  .checkbox-group input { width: auto; }
  .conditional { margin-left: 2rem; margin-top: 1rem; border-left: 3px solid #007bff; padding-left: 1rem; }
  .selection-display {
    min-height: 2rem; padding: 0.5rem; border: 1px solid #eee; border-radius: 4px;
    background-color: #f8f9fa; margin-bottom: 0.5rem; color: #666;
  }
  .select-btn {
    background-color: #6c757d; font-size: 0.9rem; padding: 0.5rem 1rem; margin-top: 0;
  }
  .form-actions {
    display: flex; justify-content: flex-end; gap: 1rem; margin-top: 1rem;
  }
  .clone-btn {
    background-color: #17a2b8;
  }
  hr {
    border: none;
    border-top: 1px solid #eee;
    margin: 1.5rem 0;
  }
  .rule-box { border: 1px dashed #ccc; padding: 1rem; border-radius: 8px; margin-bottom: 1rem; }
  .tier-box { border-color: #6610f2; background-color: #fcf9ff; }
  .rule-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
  .rule-header span { font-weight: bold; }
  .remove-btn { background-color: #dc3545; font-size: 0.8rem; padding: 4px 8px; color: white; border: none; border-radius: 4px; cursor: pointer; }
  .add-rule-btn { background-color: #28a745; margin-top: 1rem; color: white; border: none; border-radius: 4px; cursor: pointer; padding: 0.75rem; font-size: 1.1rem; }
  .no-rules-text { color: #6c757d; text-align: center; }
  .row { display: flex; gap: 1rem; }
  .header-actions { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
  .formula-btn { background: #6610f2; color: white; border: none; padding: 0.5rem 1rem; border-radius: 4px; cursor: pointer; font-size: 0.9rem; }

    .schedule-fieldset {
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 1.5rem;
        margin-bottom: 1.5rem;
        background-color: #f8fafc;
    }

    .schedule-fieldset legend {
        font-weight: bold;
        color: #2d3748;
        padding: 0 0.5rem;
        background: #fff;
        border: 1px solid #e2e8f0;
        border-radius: 4px;
    }

    .days-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
        gap: 0.5rem;
        margin-top: 0.5rem;
    }

    .day-checkbox {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.95rem;
        cursor: pointer;
        background: #fff;
        padding: 0.5rem;
        border: 1px solid #cbd5e1;
        border-radius: 4px;
        transition: background-color 0.2s;
    }

    .day-checkbox:hover {
        background-color: #f1f5f9;
    }

    .date-input-group {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
    }

    .date-input-group input {
        flex: 1;
    }

    .btn-add-date {
        padding: 0.5rem 1rem;
        background-color: #4a5568;
        color: white;
        border: none;
        border-radius: 4px;
        font-weight: 500;
        cursor: pointer;
    }

    .btn-add-date:hover {
        background-color: #2d3748;
    }

    .tags-container {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 0.5rem;
    }

    .tag {
        display: inline-flex;
        align-items: center;
        background-color: #ebf8ff;
        color: #2b6cb0;
        padding: 0.4rem 0.8rem;
        border-radius: 20px;
        font-size: 0.9rem;
        font-weight: 600;
        border: 1px solid #bee3f8;
    }

    .remove-tag {
        background: transparent;
        border: none;
        color: #e53e3e;
        font-size: 1.2rem;
        margin-left: 0.5rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        padding: 0;
        line-height: 1;
    }

    .remove-tag:hover {
        color: #9b2c2c;
    }


    .tag-ineligible {
        background-color: #fff5f5;
        color: #e53e3e;
        border: 1px solid #fed7d7;
    }

</style>